import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';

import { useGetGroupNegotiationMembers } from '@/api/endpoints/community/useGetGroupNegotiationMembers';
import { CheckVerified01Icon } from '@/assets/icons/CheckVerified01Icon';
import { XCloseIcon } from '@/assets/icons/XCloseIcon';

interface GroupMembersModalProps {
  open: boolean;
  onClose: () => void;
  communityId: number;
  negotiationId: number;
  groupTitle: string;
}

const GroupMembersModal: React.FC<GroupMembersModalProps> = ({
  open,
  onClose,
  communityId,
  negotiationId,
  groupTitle,
}) => {
  const { data: members, isLoading } = useGetGroupNegotiationMembers(
    {
      communityId,
      negotiationId,
    },
    open
  );

  const getAvatarProps = (name: string) => {
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    const colors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'] as const;
    const colorIndex = name.length % colors.length;

    return {
      children: initials,
      sx: {
        bgcolor: `${colors[colorIndex]}.main`,
        color: 'white',
        width: 40,
        height: 40,
        fontSize: '1rem',
        fontWeight: 600,
      },
    };
  };

  const formatJoinDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return 'Unknown date';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="h2">
            Members of "{groupTitle}"
          </Typography>
          <IconButton onClick={onClose} size="small">
            <XCloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {(() => {
          if (isLoading) {
            return (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Loading members...
                </Typography>
              </Box>
            );
          }
          
          if (!members || members.length === 0) {
            return (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No members found.
                </Typography>
              </Box>
            );
          }
          
          return (
            <List sx={{ pt: 0 }}>
              {members.map((member, index) => (
                <ListItem
                  key={member.id}
                  sx={{
                    borderBottom: index < members.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    py: 2,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar {...getAvatarProps(member.full_name || member.email)} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {member.full_name || member.email}
                        </Typography>
                        {member.is_verified && (
                          <CheckVerified01Icon
                            width={16}
                            height={16}
                            style={{ color: '#10B981' }} // green-500 color for verified badge
                          />
                        )}
                        {member.role === 'ADMIN' && (
                          <Chip 
                            label="Admin" 
                            size="small" 
                            color="primary"
                            sx={{ fontSize: '0.75rem', height: 20 }}
                          />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {member.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                        Joined {formatJoinDate(member.joined_at)}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
};

export default GroupMembersModal;